import React, { useEffect, useRef, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import Footer from "../../../core/common/footer/Footer";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store";
import z from "zod";
import useApi from "../../../core/customhook/useApi";
import Swal from "sweetalert2";
import SnipperLoader from "../../../core/common/loader/SnipperLoader";
import { apibaseurl } from "../../../environment";
import { triggerPackageReload } from "../../../core/data/redux/packageSlice";
import { all_routes } from "../../router/all_routes";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "../../auth/AuthContext";
import bulbImage from "../../../images/buld.png";
import { disabledStatuses } from "../../../core/common/DateValidHelper";

type EmploymentRecord = Record<string, any>;

const Reference = () => {
  const templates = useSelector((state: RootState) => state.package.templates);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { authData } = useAuth();

  const {
    assignment_template_id,
    assignment_id,
    case_id,
    candidate_id,
    actor_type,
  } = state || {};

  const referenceTemplate = templates.find(
    (item: any) => item.check_name === "Reference",
  );

  const assignmentTemplateId = referenceTemplate?.assignment_template_id;

  const caseId = referenceTemplate?.case_id;
  const assignmentId = referenceTemplate?.assignment_id;
  const candidateId = authData?.candidate?.candidateId;
  const templateStatus = referenceTemplate?.status;

  const [formFields, setFormFields] = useState<any[]>([]);
  const [checkDetails, setCheckDetails] = useState<any>();

  const [referenceHistory, setReferenceHistory] = useState<EmploymentRecord[]>(
    [],
  );

  const [employmentUploadedFiles, setReferenceUploadedFiles] = useState<
    Record<string, any>[]
  >([]);

  const [noDocument, setNoDocument] = useState<Record<string, boolean>>({});

  const [isFresher, setIsFresher] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [collapsedEmployments, setCollapsedEmployments] = useState<
    Record<number, boolean>
  >({});

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyRemarks, setReplyRemarks] = useState("");
  const [replyDocuments, setReplyDocuments] = useState<File[]>([]);

  const isFormDisabled = disabledStatuses.includes(templateStatus ?? "");
  const form_type_new = formFields?.[0]?.form_type_new;

  const createEmptyEmployment = (fields: any[]): EmploymentRecord => {
    const record: EmploymentRecord = {};

    fields.forEach((field: any) => {
      record[field.field_id] = null;
    });

    return record;
  };
  const getRecordKey = (employmentIndex: number, fieldId: string) =>
    `${employmentIndex}_${fieldId}`;

  const generateSchema = (fields: any[], employmentIndex: number) => {
    const schemaFields: Record<string, any> = {};

    fields.forEach((field) => {
      let validator: any = z.any();

      if (field.type !== "file") {
        validator = z.string();

        if (field.required) {
          validator = validator.min(1, `${field.label} is required`);
        }

        if (field?.required && field.validation_rules?.minLength) {
          validator = validator.min(
            field.validation_rules.minLength,
            `${field.label} must be at least ${field.validation_rules.minLength} characters`,
          );
        }

        if (field?.required && field.validation_rules?.maxLength) {
          validator = validator.max(
            field.validation_rules.maxLength,
            `${field.label} cannot exceed ${field.validation_rules.maxLength} characters`,
          );
        }

        if (field?.required && field.validation_rules?.pattern) {
          validator = validator.regex(
            new RegExp(field.validation_rules.pattern),
            `${field.label} is invalid`,
          );
        }
      }

      if (field.type === "file") {
        const key = getRecordKey(employmentIndex, field.field_id);

        const skipValidation = noDocument?.[key] === true;

        const isMultiple = field.file_config?.multiple === true;

        const existingFiles =
          employmentUploadedFiles[employmentIndex]?.[field.field_id];

        const hasExistingFiles =
          Array.isArray(existingFiles) && existingFiles.length > 0;

        if (skipValidation || hasExistingFiles) {
          validator = z.any().optional();
        } else {
          validator = z.preprocess(
            (value) => {
              // Accept blank, null, or undefined values
              if (value === "" || value === null || value === undefined) {
                return isMultiple ? [] : null;
              }

              // Accept a single File for multiple-file fields
              if (isMultiple && value instanceof File) {
                return [value];
              }

              return value;
            },
            isMultiple
              ? field.required
                ? z
                    .array(z.instanceof(File))
                    .min(1, `${field.label} is required`)
                : z.array(z.instanceof(File))
              : field.required
                ? z.instanceof(File, {
                    message: `${field.label} is required`,
                  })
                : z.instanceof(File).nullable(),
          );
        }
      }

      schemaFields[field.field_id] = validator;
    });

    return z.object(schemaFields);
  };

  const formFieldsAPI = useApi<any>(
    `/api/v1/candidate/templates/${assignmentTemplateId}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const templateSaveAPI = useApi<any>(
    `/api/v1/candidate/templates/${assignmentTemplateId}/save`,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const templateUpdateAPI = useApi<any>(
    `/api/v1/candidate/forms/filled/${assignmentTemplateId}`,
    {
      method: "PUT",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const insuffResponse = useApi<any>(
    `/api/v1/insufficiency/candidate-response`,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const referenceForm = async () => {
    try {
      const response = await formFieldsAPI.callApi();

      if (response?.success) {
        const checkdetails = response?.data?.check;

        handleEmploymentResponse(response);

        setCheckDetails(checkdetails);
        setErrors({});
        setCollapsedEmployments({});
        setNoDocument({});
      }
    } catch (error) {
      console.error("Error fetching form fields:", error);
    }
  };

  const formLoading = formFieldsAPI.loading;

  useEffect(() => {
    if (assignmentTemplateId) {
      referenceForm();
    }
  }, [assignmentTemplateId]);

  const handleEmploymentResponse = (response: any) => {
    const fields = response?.data?.fields || [];

    if (!fields.length) {
      setFormFields([]);
      setReferenceHistory([]);
      setReferenceUploadedFiles([]);
      return;
    }

    // AFTER SAVE / EXISTING EMPLOYMENT RECORDS
    if (fields[0]?.fields && Array.isArray(fields[0].fields)) {
      setFormFields(fields[0].fields);

      const employmentRecords = fields.map((entry: any) => {
        const record: Record<string, any> = {};

        entry.fields.forEach((field: any) => {
          record[field.field_id] = field.value ?? null;
        });

        return record;
      });

      // Store uploaded files separately for each employment record
      const uploadedFilesByEmployment = fields.map((entry: any) => {
        const files: Record<string, any> = {};

        entry.fields.forEach((field: any) => {
          if (
            Array.isArray(field.uploaded_files) &&
            field.uploaded_files.length > 0
          ) {
            files[field.field_id] = field.uploaded_files;
          }
        });

        return files;
      });

      setReferenceHistory(employmentRecords);
      setReferenceUploadedFiles(uploadedFilesByEmployment);

      return;
    }

    // FIRST TIME
    setFormFields(fields);

    setReferenceHistory([createEmptyEmployment(fields)]);

    // No uploaded files for a new/blank employment
    setReferenceUploadedFiles([{}]);
  };

  const handleFieldChange = async (
    employmentIndex: number,
    fieldId: string,
    value: any,
  ) => {
    setReferenceHistory((prev) => {
      const updated = [...prev];

      const currentRecord = {
        ...(updated[employmentIndex] || {}),
      };

      currentRecord[fieldId] = value;

      // Supervisor Details
      if (fieldId === "supervisor_details_available") {
        if (value === "NO") {
          currentRecord.reporting_manager_name = "N/A";
          currentRecord.reporting_manager_email = "N/A";
          currentRecord.reporting_manager_mobile = "N/A";
          currentRecord.reporting_manager_designation = "N/A";
        } else {
          currentRecord.reporting_manager_name = "";
          currentRecord.reporting_manager_email = "";
          currentRecord.reporting_manager_mobile = "";
          currentRecord.reporting_manager_designation = "";
        }
      }

      // HR Details
      if (fieldId === "hr_details_available") {
        if (value === "NO") {
          currentRecord.hr_name = "N/A";
          currentRecord.hr_contact_details = "N/A";
          currentRecord.hr_email_id = "N/A";
        } else {
          currentRecord.hr_name = "";
          currentRecord.hr_contact_details = "";
          currentRecord.hr_email_id = "";
        }
      }

      // Pincode
      if (fieldId === "pincode" && String(value).length < 6) {
        currentRecord.organization_state = "";
        currentRecord.organization_city = "";
      }

      updated[employmentIndex] = currentRecord;

      return updated;
    });

    const errorKey = getRecordKey(employmentIndex, fieldId);

    setErrors((prev) => ({
      ...prev,
      [errorKey]: "",
    }));
  };

  const addReference = () => {
    const newRecord = createEmptyEmployment(formFields);

    setReferenceHistory((prev) => [...prev, newRecord]);

    // New employment has no uploaded files
    setReferenceUploadedFiles((prev) => [...prev, {}]);

    setCollapsedEmployments((prev) => ({
      ...prev,
      [referenceHistory.length]: false,
    }));
  };
  const removeReference = (employmentIndex: number) => {
    if (referenceHistory.length === 1) {
      Swal.fire({
        icon: "warning",
        title: "Cannot Remove",
        text: "At least one reference record is required.",
      });

      return;
    }

    Swal.fire({
      icon: "warning",
      title: "Remove Reference?",
      text: `Are you sure you want to remove Reference ${employmentIndex + 1}?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Remove",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#D92D20",
      cancelButtonColor: "#667085",
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) return;

      setReferenceHistory((prev) =>
        prev.filter((_, index) => index !== employmentIndex),
      );
      setReferenceUploadedFiles((prev) =>
        prev.filter((_, index) => index !== employmentIndex),
      );

      setErrors((prev) => {
        const updated: Record<string, string> = {};

        Object.keys(prev).forEach((key) => {
          const [indexString, fieldId] = key.split("_");

          const index = Number(indexString);

          if (index < employmentIndex) {
            updated[key] = prev[key];
          } else if (index > employmentIndex) {
            updated[`${index - 1}_${fieldId}`] = prev[key];
          }
        });

        return updated;
      });
    });
  };

  const toggleReference = (employmentIndex: number) => {
    setCollapsedEmployments((prev) => ({
      ...prev,
      [employmentIndex]: !prev[employmentIndex],
    }));
  };

  const validateReferenceHistory = () => {
    const allErrors: Record<string, string> = {};

    let hasError = false;

    referenceHistory.forEach((employment, employmentIndex) => {
      const schema = generateSchema(formFields, employmentIndex);

      // Convert null/undefined values to empty strings before validation.
      const normalizedEmployment = Object.fromEntries(
        Object.entries(employment).map(([fieldId, value]) => [
          fieldId,
          value == null ? "" : value,
        ]),
      );

      const validationResult = schema.safeParse(normalizedEmployment);

      if (!validationResult.success) {
        hasError = true;

        validationResult.error.issues.forEach((issue) => {
          const fieldName = issue.path[0] as string;
          const errorKey = getRecordKey(employmentIndex, fieldName);

          if (!allErrors[errorKey]) {
            allErrors[errorKey] = issue.message;
          }
        });
      }
    });

    setErrors(allErrors);

    if (hasError) {
      const firstErrorKey = Object.keys(allErrors)[0];

      if (firstErrorKey) {
        const [firstIndex] = firstErrorKey.split("_");

        setCollapsedEmployments((prev) => ({
          ...prev,
          [Number(firstIndex)]: false,
        }));
      }

      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Please complete all required fields before saving.",
      });

      return false;
    }

    return true;
  };

  const buildPayload = () => {
    const payload = new FormData();

    // =====================================================
    // CREATE JSON DATA WITHOUT FILE / FILE[]
    // =====================================================

    const jsonData = referenceHistory.map((employment) => {
      const record: Record<string, any> = {};

      Object.keys(employment).forEach((fieldId) => {
        const value = employment[fieldId];

        // Single binary file
        if (value instanceof File) {
          return;
        }

        // Multiple binary files
        if (
          Array.isArray(value) &&
          value.some((item) => item instanceof File)
        ) {
          return;
        }

        // Normal field
        record[fieldId] = value ?? "";
      });

      return record;
    });

    // =====================================================
    // APPEND FILES AS BINARY
    // =====================================================

    referenceHistory.forEach((employment, employmentIndex) => {
      Object.keys(employment).forEach((fieldId) => {
        const value = employment[fieldId];

        const fileKey = `reference_${employmentIndex}_${fieldId}`;

        // -----------------------------
        // SINGLE FILE
        // -----------------------------
        if (value instanceof File) {
          payload.append(fileKey, value);
          return;
        }

        // -----------------------------
        // MULTIPLE FILES
        // -----------------------------
        if (Array.isArray(value)) {
          value.forEach((file) => {
            if (file instanceof File) {
              payload.append(fileKey, file);
            }
          });
        }
      });
    });

    // =====================================================
    // NORMAL JSON DATA
    // =====================================================

    payload.append("formData", JSON.stringify(jsonData));

    payload.append("form_type", form_type_new);

    return payload;
  };

  const handleSaveDocument = async () => {
    try {
      const isValid = validateReferenceHistory();

      if (!isValid) {
        return;
      }

      setErrors({});

      const payload = buildPayload();

      const response = await templateSaveAPI.callApi(payload);

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.message || "Document saved successfully",
          confirmButtonText: "OK",
        });

        dispatch(triggerPackageReload());

        await referenceForm();
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text:
          error?.response?.data?.error ||
          error?.message ||
          "Something went wrong",
      });
    }
  };

  const handleUpdateDocument = async () => {
    try {
      if (!isFresher) {
        const isValid = validateReferenceHistory();

        if (!isValid) {
          return;
        }
      }

      setErrors({});

      const payload = buildPayload();

      const response = await templateUpdateAPI.callApi(payload);

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.message || "Document update successfully",
          confirmButtonText: "OK",
        });

        // navigate(all_routes.caseSummary);
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error?.response?.data?.error ||
          error?.message ||
          "Something went wrong",
      });
    }
  };

  const handleReplyFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    setReplyDocuments(Array.from(e.target.files));
  };

  const removeReplyFile = (index: number) => {
    setReplyDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitReply = async () => {
    if (!replyRemarks.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Remarks Required",
        text: "Please enter your remarks before submitting.",
      });

      return;
    }

    try {
      const payload = new FormData();

      payload.append(
        "assignment_template_id",
        assignment_template_id ?? assignmentTemplateId ?? "",
      );

      payload.append("assignment_id", assignment_id ?? assignmentId ?? "");

      payload.append("case_id", case_id ?? caseId ?? "");

      payload.append("candidate_id", candidate_id ?? candidateId ?? "");

      payload.append("actor_due_on", actor_type ?? "CANDIDATE");

      payload.append("remarks", replyRemarks);

      replyDocuments.forEach((file) => {
        payload.append("documents", file);
      });

      const res = await insuffResponse.callApi(payload);

      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Your response has been submitted successfully.",
        });

        navigate(all_routes.caseSummary);
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: error?.message || "Unable to submit your response.",
      });
    }
  };

  const sortedNormalFields = formFields
    .filter((field) => field.type !== "file")
    .slice()
    .sort((a, b) => a.display_order - b.display_order);

  const sortedFileFields = formFields
    .filter((field) => field.type === "file")
    .slice()
    .sort((a, b) => a.display_order - b.display_order);

  const getYesterdayDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 1);

    return date.toLocaleDateString("en-CA");
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-8">
              {formLoading ? (
                <SnipperLoader />
              ) : (
                <div className="card overflow-hidden">
                  {/* Header */}
                  <div
                    className="border-bottom px-4 py-3"
                    style={{
                      backgroundColor: "#FAFAFA",
                    }}
                  >
                    <h5
                      className="fw-semibold mb-1"
                      style={{
                        color: "#101828",
                        fontSize: "17px",
                      }}
                    >
                      {checkDetails?.check_name}
                    </h5>
                  </div>

                  <div className="p-4">
                    {/* Reference Cards */}
                    {referenceHistory.map((employment, employmentIndex) => {
                      const isCollapsed = collapsedEmployments[employmentIndex];

                      return (
                        <div
                          key={employmentIndex}
                          className="border rounded-3 mb-4 overflow-hidden"
                          style={{
                            background: "#FFFFFF",
                            borderColor: "#D0D5DD",
                          }}
                        >
                          {/* Card Header */}
                          <div
                            className="px-4 py-3 d-flex align-items-center justify-content-between"
                            style={{
                              background: "#F8FAFC",
                              borderBottom: isCollapsed
                                ? "none"
                                : "1px solid #EAECF0",
                              cursor: "pointer",
                            }}
                            onClick={() => toggleReference(employmentIndex)}
                          >
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle"
                                style={{
                                  width: "34px",
                                  height: "34px",
                                  background: "#E1F5EE",
                                  color: "#0F6E56",
                                  fontWeight: 600,
                                }}
                              >
                                {employmentIndex + 1}
                              </div>

                              <div>
                                <h6 className="fw-semibold">
                                  Reference {employmentIndex + 1}
                                </h6>
                              </div>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                              {referenceHistory.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-light text-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();

                                    removeReference(employmentIndex);
                                  }}
                                  disabled={isFormDisabled}
                                >
                                  <i className="ti ti-trash me-1"></i>
                                  Remove
                                </button>
                              )}

                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={(e) => {
                                  e.stopPropagation();

                                  toggleReference(employmentIndex);
                                }}
                              >
                                <i
                                  className={`ti ${
                                    isCollapsed
                                      ? "ti-chevron-down"
                                      : "ti-chevron-up"
                                  }`}
                                ></i>
                              </button>
                            </div>
                          </div>

                          {!isCollapsed && (
                            <div className="p-4">
                              {/* Dynamic Fields */}
                              <div className="row mb-3 ">
                                {sortedNormalFields.map((field) => {
                                  const errorKey = getRecordKey(
                                    employmentIndex,
                                    field.field_id,
                                  );

                                  const fieldValue =
                                    employment[field.field_id] ?? "";

                                  return (
                                    <div
                                      key={`${employmentIndex}_${field.field_uuid}`}
                                      className="col-md-6 d-flex flex-column"
                                    >
                                      <label className="form-label fw-medium">
                                        {field.label}

                                        {field.required && (
                                          <span className="text-danger ms-1">
                                            *
                                          </span>
                                        )}
                                      </label>

                                      {field.type === "dropdown" ? (
                                        <select
                                          className={`form-select ${
                                            errors[errorKey] ? "is-invalid" : ""
                                          }`}
                                          value={isFresher ? "N/A" : fieldValue}
                                          required={field.required}
                                          disabled={isFormDisabled}
                                          onChange={(e) =>
                                            handleFieldChange(
                                              employmentIndex,
                                              field.field_id,
                                              e.target.value,
                                            )
                                          }
                                        >
                                          <option value="">
                                            {field.placeholder || "Select"}
                                          </option>

                                          {field.options?.map((option: any) => (
                                            <option
                                              key={option.value}
                                              value={option.value}
                                            >
                                              {option.label}
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <input
                                          type={field.type}
                                          className={`form-control ${
                                            errors[errorKey] ? "is-invalid" : ""
                                          }`}
                                          placeholder={field.placeholder}
                                          value={isFresher ? "N/A" : fieldValue}
                                          required={field.required}
                                          disabled={isFormDisabled}
                                          onChange={(e) =>
                                            handleFieldChange(
                                              employmentIndex,
                                              field.field_id,
                                              e.target.value,
                                            )
                                          }
                                          pattern={
                                            field?.validation_rules?.pattern
                                          }
                                          maxLength={
                                            field?.validation_rules?.maxLength
                                          }
                                          minLength={
                                            field?.validation_rules?.minLength
                                          }
                                        />
                                      )}

                                      {errors[errorKey] && (
                                        <div className="invalid-feedback d-block">
                                          {errors[errorKey]}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>

                              {/* Documents */}
                              {sortedFileFields.length > 0 && (
                                <div className="border-top pt-4 mt-2">
                                  {sortedFileFields.map((field) => {
                                    const key = getRecordKey(
                                      employmentIndex,
                                      field.field_id,
                                    );

                                    // const selectedFile =
                                    //   employment[field.field_id];

                                    // const isNoDocument = noDocument[key];

                                    // const uploadedFile =
                                    //   field.uploaded_files?.[0];
                                    const selectedFile =
                                      employment[field.field_id];

                                    const isNoDocument = noDocument[key];

                                    const uploadedFiles =
                                      employmentUploadedFiles[
                                        employmentIndex
                                      ]?.[field.field_id] || [];

                                    return (
                                      <div
                                        key={`${employmentIndex}_${field.field_uuid}`}
                                        className="row align-items-center mb-4 pb-4 border-bottom"
                                      >
                                        <div className="col-lg-7">
                                          <label className="form-label fw-semibold">
                                            {field.label}

                                            {field.required && (
                                              <span className="text-danger ms-1">
                                                *
                                              </span>
                                            )}
                                          </label>

                                          <input
                                            ref={(el) => {
                                              fileInputRefs.current[key] = el;
                                            }}
                                            type="file"
                                            className={`form-control ${
                                              errors[key] ? "is-invalid" : ""
                                            }`}
                                            multiple={
                                              field.file_config?.multiple ===
                                              true
                                            }
                                            accept={field.file_config?.allowedTypes
                                              ?.map(
                                                (type: string) => `.${type}`,
                                              )
                                              .join(",")}
                                            onChange={(e) => {
                                              if (field.file_config?.multiple) {
                                                handleFieldChange(
                                                  employmentIndex,
                                                  field.field_id,
                                                  Array.from(
                                                    e.target.files || [],
                                                  ),
                                                );
                                              } else {
                                                handleFieldChange(
                                                  employmentIndex,
                                                  field.field_id,
                                                  e.target.files?.[0] || null,
                                                );
                                              }
                                            }}
                                            disabled={
                                              isNoDocument || isFormDisabled
                                            }
                                          />

                                          {errors[key] && (
                                            <div className="invalid-feedback d-block">
                                              {errors[key]}
                                            </div>
                                          )}
                                        </div>

                                        <div className="col-lg-5 text-center mt-3 mt-lg-0">
                                          {/* ========================================== */}
                                          {/* NEWLY SELECTED MULTIPLE FILES */}
                                          {/* ========================================== */}

                                          {Array.isArray(selectedFile) &&
                                          selectedFile.length > 0 ? (
                                            <>
                                              <i
                                                className="bi bi-file-earmark-pdf-fill text-danger"
                                                style={{
                                                  fontSize: "50px",
                                                  cursor: "pointer",
                                                }}
                                              />

                                              {selectedFile.map(
                                                (file: File, index: number) => (
                                                  <p
                                                    key={`${file.name}-${index}`}
                                                    className="text-danger small mb-0 text-truncate"
                                                    title={file.name}
                                                  >
                                                    {file.name}
                                                  </p>
                                                ),
                                              )}
                                            </>
                                          ) : selectedFile instanceof File ? (
                                            /* ========================================== */
                                            /* NEWLY SELECTED SINGLE FILE */
                                            /* ========================================== */

                                            <>
                                              <i
                                                className="bi bi-file-earmark-pdf-fill text-danger"
                                                style={{
                                                  fontSize: "50px",
                                                  cursor: "pointer",
                                                }}
                                              />

                                              <p
                                                className="text-danger small mb-0 text-truncate"
                                                title={selectedFile.name}
                                              >
                                                {selectedFile.name}
                                              </p>
                                            </>
                                          ) : uploadedFiles.length > 0 ? (
                                            /* ========================================== */
                                            /* EXISTING UPLOADED FILES */
                                            /* ========================================== */

                                            <>
                                              <i
                                                className="bi bi-file-earmark-pdf-fill text-danger"
                                                style={{
                                                  fontSize: "48px",
                                                }}
                                              />

                                              {uploadedFiles.map(
                                                (file: any, index: number) => (
                                                  <a
                                                    key={
                                                      file.id ??
                                                      `${file.original_name}-${index}`
                                                    }
                                                    href={`${apibaseurl}${file.url}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-decoration-none d-block"
                                                  >
                                                    <p
                                                      className="text-danger small mb-0 text-truncate"
                                                      title={
                                                        file.original_name ||
                                                        file.name
                                                      }
                                                    >
                                                      {file.original_name ||
                                                        file.name ||
                                                        `Document ${index + 1}`}
                                                    </p>
                                                  </a>
                                                ),
                                              )}
                                            </>
                                          ) : (
                                            /* ========================================== */
                                            /* NO FILE */
                                            /* ========================================== */

                                            <>
                                              <i
                                                className="bi bi-file-earmark-pdf-fill text-danger"
                                                style={{
                                                  fontSize: "48px",
                                                }}
                                              />

                                              <p className="text-muted small mb-0">
                                                No File Selected
                                              </p>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Add Reference */}
                    <button
                      type="button"
                      className="w-100 mb-4"
                      onClick={addReference}
                      disabled={isFormDisabled}
                      style={{
                        border: "1px dashed #98A2B3",
                        background: "#F8FAFC",
                        borderRadius: "10px",
                        padding: "14px 20px",
                        cursor: isFormDisabled ? "not-allowed" : "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (!isFormDisabled) {
                          e.currentTarget.style.background = "#F0FDF9";
                          e.currentTarget.style.borderColor = "#0F6E56";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#F8FAFC";
                        e.currentTarget.style.borderColor = "#98A2B3";
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-center gap-3">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle"
                          style={{
                            width: "34px",
                            height: "34px",
                            background: "#E1F5EE",
                            color: "#0F6E56",
                            flexShrink: 0,
                          }}
                        >
                          <i
                            className="ti ti-plus"
                            style={{ fontSize: "18px" }}
                          ></i>
                        </div>

                        <div className="text-start">
                          <div
                            className="fw-semibold"
                            style={{
                              color: "#344054",
                              fontSize: "14px",
                            }}
                          >
                            Add Reference
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* Save */}
                    {!isFormDisabled &&
                      templateStatus !== "CANDIDATE_INSUFF" && (
                        <div className="d-flex gap-2 align-items-center mt-4">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSaveDocument}
                            disabled={templateSaveAPI.loading}
                          >
                            {templateSaveAPI.loading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="ti ti-device-floppy me-2"></i>
                                Save
                              </>
                            )}
                          </button>
                        </div>
                      )}

                    {/* Insufficiency Update */}
                    {templateStatus === "CANDIDATE_INSUFF" && (
                      <div className="d-flex gap-2 align-items-center mt-4">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleUpdateDocument}
                          disabled={templateUpdateAPI.loading}
                        >
                          {templateUpdateAPI.loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Updating...
                            </>
                          ) : (
                            <>
                              <i className="ti ti-upload me-2"></i>
                              Save Form & Documents
                            </>
                          )}
                        </button>

                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={() => setShowReplyModal(true)}
                        >
                          <i className="ti ti-message-circle me-2"></i>
                          Reply to Insufficiency
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="col-md-4">
              <div
                style={{
                  position: "sticky",
                  top: "60px",
                  alignSelf: "flex-start",
                }}
              >
                <div className="card border-0 shadow-sm">
                  {/* Header */}
                  <div
                    className="text-center py-4"
                    style={{ backgroundColor: "#f7f1e3" }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center mx-auto mb-2 rounded-circle bg-white"
                      style={{ width: "70px", height: "70px" }}
                    >
                      {/* <i
                                className="bi bi-lightbulb-fill text-warning"
                                style={{ fontSize: "35px" }}
                              ></i> */}
                      <img src={bulbImage} alt="buld" />
                    </div>

                    <h6 className="fw-bold mb-0">Instructions</h6>
                  </div>

                  {/* Body */}
                  <div className="card-body">
                    {/* Instruction 1 */}
                    <div className="d-flex mb-4">
                      <div
                        className="rounded me-3 mt-1 flex-shrink-0"
                        style={{
                          width: "16px",
                          height: "16px",
                          backgroundColor: "#f4c542",
                        }}
                      ></div>

                      <div>
                        <h6 className="mb-1 fw-semibold">Instruction 1</h6>

                        <p className="text-muted mb-0 small">
                          Kindly fill in and submit the form at the earliest,
                          this form will expire in 15 days, in case there is no
                          activity.
                        </p>
                      </div>
                    </div>

                    {/* Instruction 2 */}
                    <div className="d-flex mb-4">
                      <div
                        className="rounded me-3 mt-1 flex-shrink-0"
                        style={{
                          width: "16px",
                          height: "16px",
                          backgroundColor: "#f4c542",
                        }}
                      ></div>

                      <div>
                        <h6 className="mb-1 fw-semibold">Instruction 2</h6>

                        <p className="text-muted mb-0 small">
                          In case you are not joining the company, kindly inform
                          the HR to stop your background verification process.
                        </p>
                      </div>
                    </div>

                    {/* Instruction 3
                          <div className="d-flex mb-4">
                            <div
                              className="rounded me-3 mt-1 flex-shrink-0"
                              style={{
                                width: "16px",
                                height: "16px",
                                backgroundColor: "#f4c542",
                              }}
                            ></div>
          
                            <div>
                              <h6 className="mb-1 fw-semibold">Instruction 3</h6>
          
                              <p className="text-muted mb-0 small">
                                Please provide all the information/documentation at the
                                time of filling the form to avoid any delay in processing.
                                In case you do not have the document, you can still proceed
                                and we will raise a request at a later timeframe if
                                required.
                              </p>
                            </div>
                          </div>
          
                          Instruction 4
                          <div className="d-flex">
                            <div
                              className="rounded me-3 mt-1 flex-shrink-0"
                              style={{
                                width: "16px",
                                height: "16px",
                                backgroundColor: "#f4c542",
                              }}
                            ></div>
          
                            <div>
                              <h6 className="mb-1 fw-semibold">Instruction 4</h6>
          
                              <p className="text-muted mb-0 small">
                                Please upload photograph, Front facing with clear
                                background. 3MB size limit with Jpeg, Png and jpg format
                                only.
                              </p>
                            </div>
                          </div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reply to Insufficiency Modal */}
          {showReplyModal && (
            <div
              className="modal fade show d-block"
              style={{
                background: "rgba(0,0,0,.5)",
              }}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content border-0 shadow">
                  <div className="modal-header">
                    <div>
                      <h5 className="modal-title fw-bold mb-1">
                        Respond to Insufficiency
                      </h5>

                      <small className="text-muted">
                        Please provide your explanation and upload supporting
                        documents if applicable.
                      </small>
                    </div>

                    <button
                      className="btn-close"
                      onClick={() => setShowReplyModal(false)}
                    />
                  </div>

                  <div className="modal-body">
                    <div className="mb-4">
                      <label className="form-label fw-semibold">
                        Remarks
                        <span className="text-danger ms-1">*</span>
                      </label>

                      <textarea
                        rows={5}
                        className="form-control"
                        placeholder="Enter your response to the verifier..."
                        value={replyRemarks}
                        onChange={(e) => setReplyRemarks(e.target.value)}
                        style={{
                          resize: "none",
                        }}
                      />

                      <small className="text-muted">
                        Clearly explain the requested information or
                        clarification.
                      </small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Supporting Documents
                      </label>

                      <input
                        type="file"
                        className="form-control"
                        multiple
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={handleReplyFileChange}
                      />

                      <small className="text-muted">
                        Accepted formats: JPG, JPEG, PNG, PDF. Maximum 10 MB per
                        file.
                      </small>
                    </div>

                    {replyDocuments.length > 0 && (
                      <div className="mt-3">
                        <label className="fw-semibold mb-2">
                          Selected Files
                        </label>

                        <div className="d-flex flex-wrap gap-2">
                          {replyDocuments.map((file, index) => (
                            <span
                              key={index}
                              className="badge rounded-pill bg-success-subtle text-dark border border-success-subtle px-3 py-2 d-flex align-items-center"
                            >
                              <i className="ti ti-paperclip me-2"></i>

                              <span
                                className="text-truncate"
                                style={{
                                  maxWidth: 180,
                                }}
                              >
                                {file.name}
                              </span>

                              <button
                                type="button"
                                className="btn btn-sm border-0 bg-transparent text-danger ms-2 p-0"
                                onClick={() => removeReplyFile(index)}
                              >
                                <i className="ti ti-x"></i>
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="modal-footer gap-3">
                    <button
                      className="btn btn-light border"
                      onClick={() => setShowReplyModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className="btn btn-primary"
                      onClick={handleSubmitReply}
                      disabled={insuffResponse.loading}
                    >
                      {insuffResponse.loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-send me-2"></i>
                          Submit Response
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <Footer />
      </div>
    </>
  );
};

export default Reference;
